import { safeGetItem, safeSetItem } from "@/lib/safe-storage";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Briefcase,
  CalendarDays,
  Clock,
  FileSpreadsheet,
  HeartPulse,
  Inbox,
  LineChart,
  ListChecks,
  NotebookPen,
  PieChart,
  Presentation,
  Activity,
  Repeat,
  RefreshCcw,
  Sparkle,
  Target,
  Users,
  Handshake,
  History,
  LayoutDashboard,
  Settings,
  Tags,
  UploadCloud,
  Wallet,
  Building2,
  Contact,
  GanttChartSquare,
  type LucideIcon,
} from "lucide-react";
import {
  MENU_POSITION_EVENT,
  MENU_POSITION_KEY,
  NAV_EVENT,
  NAV_STORAGE_KEY,
  pushMenuPosition,
  pushNavPrimary,
} from "@/lib/prefs-sync";
import { useFeatures } from "@/lib/persona";

/**
 * A single menu entry. `id` is the stable identity used in saved preferences —
 * it differs from `to` only where the same page appears under two groups
 * (Pipeline lives under both Customers and Revenue, for example).
 */
export type NavItem = { id: string; to: string; label: string; icon: LucideIcon };

export type NavGroupKey = "priorities" | "customers" | "revenue" | "tasks";

export type NavGroup = {
  key: NavGroupKey;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
};

const item = (id: string, to: string, label: string, icon: LucideIcon): NavItem => ({
  id,
  to,
  label,
  icon,
});

/** The four top-level sections and everything that sits under each of them. */
export const NAV_GROUPS: NavGroup[] = [
  {
    key: "priorities",
    label: "Priorities",
    icon: Target,
    items: [
      item("priorities:dashboard", "/overview", "Dashboard", LayoutDashboard),
      item("priorities:pillars", "/pillars", "Pillars & goals", Target),
      item("priorities:progress", "/performance", "Progress", BarChart3),
      item("priorities:habits", "/habits", "Habits", HeartPulse),
    ],
  },
  {
    key: "customers",
    label: "Customers",
    icon: Users,
    items: [
      item("customers:engagement", "/sales/engagement", "Engagement", HeartPulse),
      item("customers:inbox", "/sales/inbox", "Inbox", Inbox),
      item("customers:accounts", "/sales/accounts", "Accounts", Building2),
      item("customers:contacts", "/sales/contacts", "Contacts", Contact),
      item("customers:renewals", "/sales/renewals", "Renewals & QBRs", RefreshCcw),

      item("customers:meetings", "/sales/customer-meetings", "Meetings", Repeat),
      item("customers:tasks", "/sales/customer-tasks", "Tasks", ListChecks),
      item("customers:hours", "/sales/customer-hours", "Hours per customer", Clock),
    ],
  },
  {
    key: "revenue",
    label: "Revenue",
    icon: Handshake,
    items: [
      item("revenue:overview", "/sales", "Sales overview", Handshake),
      item("revenue:opportunities", "/sales/opportunities", "Opportunities", Tags),
      item("revenue:contracts", "/sales/contracts", "Contracts", FileSpreadsheet),
      item("revenue:pipeline", "/sales/pipeline", "Pipeline", LineChart),
      item("revenue:forecast", "/sales/forecast", "Forecast", BarChart3),
      item("revenue:plan", "/sales/revenue-plan", "Revenue plan", Target),
      item("revenue:calendar", "/sales/calendar", "Sales calendar", CalendarDays),
      item("revenue:business-case", "/visuals/business-case", "Business case builder", PieChart),
      item("revenue:price-book", "/visuals/price-book", "Price book builder", Wallet),
      item("revenue:decks", "/reports", "Account plans & QBRs", Presentation),
      item("revenue:team", "/team", "Sales team", Users),
      item("revenue:import", "/sales/import", "Import", UploadCloud),
      item("revenue:import-hubspot", "/sales/admin/import-hubspot", "HubSpot import", UploadCloud),
      item("revenue:import-history", "/sales/admin/import-history", "Import history", History),
      item("revenue:settings", "/sales/settings", "Sales settings", Settings),
    ],
  },
  {
    key: "tasks",
    label: "Tasks",
    icon: Briefcase,
    items: [
      item("tasks:work", "/work", "Work hub", Briefcase),
      item("tasks:meetings", "/work/regular-meetings", "Regular meetings", Repeat),
      item("tasks:timeline", "/work/timeline", "Timeline", GanttChartSquare),
      item("tasks:inbox", "/inbox", "Inbox", Inbox),
      item("tasks:visuals", "/visuals", "Tools & visuals", PieChart),
      item("tasks:notes", "/notes", "Notes", NotebookPen),
      item("tasks:assistant", "/assistant", "Assistant", Sparkle),
      item("tasks:status", "/status", "Status", Activity),
    ],
  },
];

export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

const byId = new Map(NAV_ITEMS.map((i) => [i.id, i]));

/** Everything is visible by default apart from the deeper admin/utility pages. */
const HIDDEN_BY_DEFAULT = new Set([
  "revenue:import-history",
  "revenue:import-hubspot",
  "tasks:status",
  "priorities:habits",
]);

export const DEFAULT_PRIMARY = NAV_ITEMS.filter((i) => !HIDDEN_BY_DEFAULT.has(i.id)).map(
  (i) => i.id,
);

/** Older preferences stored plain paths; map those onto the first matching item. */
function migrateId(value: string): string | null {
  if (byId.has(value)) return value;
  const match = NAV_ITEMS.find((i) => i.to === value);
  return match ? match.id : null;
}

export const MENU_POSITIONS = ["top", "left", "right", "bottom"] as const;
export type MenuPosition = (typeof MENU_POSITIONS)[number];

function readPosition(): MenuPosition {
  if (typeof window === "undefined") return "top";
  const raw = safeGetItem(MENU_POSITION_KEY);
  return (MENU_POSITIONS as readonly string[]).includes(raw ?? "")
    ? (raw as MenuPosition)
    : "top";
}

/** Where the menu sits for this account: top, left, right or bottom of the page. */
export function useMenuPosition() {
  const [position, setPosition] = useState<MenuPosition>("top");

  useEffect(() => {
    setPosition(readPosition());
    const sync = () => setPosition(readPosition());
    window.addEventListener(MENU_POSITION_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(MENU_POSITION_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const setMenuPosition = useCallback((next: MenuPosition) => {
    safeSetItem(MENU_POSITION_KEY, next);
    window.dispatchEvent(new Event(MENU_POSITION_EVENT));
    setPosition(next);
    pushMenuPosition(next);
  }, []);

  return { position, setMenuPosition };
}

const STORAGE_KEY = NAV_STORAGE_KEY;
const EVENT = NAV_EVENT;

function read(): string[] {
  if (typeof window === "undefined") return DEFAULT_PRIMARY;
  try {
    const raw = safeGetItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PRIMARY;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return DEFAULT_PRIMARY;
    const valid = parsed
      .filter((v): v is string => typeof v === "string")
      .map(migrateId)
      .filter((v): v is string => Boolean(v));
    return valid.filter((v, i) => valid.indexOf(v) === i);
  } catch {
    return DEFAULT_PRIMARY;
  }
}

export type NavGroupView = {
  key: NavGroupKey;
  label: string;
  icon: LucideIcon;
  /** Shown in the menu, in the user's order. */
  items: NavItem[];
  /** In this group but switched off. */
  hidden: NavItem[];
};

/** Which items show under each of the four menu groups, and in what order. */
export function useNavPreferences() {
  const [primary, setPrimary] = useState<string[]>(DEFAULT_PRIMARY);
  const features = useFeatures();


  useEffect(() => {
    setPrimary(read());
    const sync = () => setPrimary(read());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const save = useCallback((next: string[]) => {
    // Stored order is authoritative — do not re-sort to the canonical list.
    const ordered = next.filter((id, i) => byId.has(id) && next.indexOf(id) === i);
    safeSetItem(STORAGE_KEY, JSON.stringify(ordered));
    window.dispatchEvent(new Event(EVENT));
    setPrimary(ordered);
    pushNavPrimary(ordered);
  }, []);

  const toggle = useCallback(
    (id: string) => {
      const current = read();
      save(current.includes(id) ? current.filter((t) => t !== id) : [...current, id]);
    },
    [save],
  );

  /** Move an item within its own group; positions in other groups stay put. */
  const move = useCallback(
    (id: string, direction: -1 | 1) => {
      const current = read();
      const group = NAV_GROUPS.find((g) => g.items.some((i) => i.id === id));
      if (!group) return;
      const inGroup = current.filter((c) => group.items.some((i) => i.id === c));
      const at = inGroup.indexOf(id);
      const target = at + direction;
      if (at < 0 || target < 0 || target >= inGroup.length) return;
      const swapWith = inGroup[target]!;
      const next = [...current];
      const a = next.indexOf(id);
      const b = next.indexOf(swapWith);
      next[a] = swapWith;
      next[b] = id;
      save(next);
    },
    [save],
  );

  /** Drop `id` at another item's slot inside the same group. */
  const reorder = useCallback(
    (id: string, targetId: string) => {
      const current = read();
      const group = NAV_GROUPS.find((g) => g.items.some((i) => i.id === id));
      if (!group || !group.items.some((i) => i.id === targetId) || id === targetId) return;
      const inGroup = current.filter((c) => group.items.some((i) => i.id === c));
      const from = inGroup.indexOf(id);
      const to = inGroup.indexOf(targetId);
      if (from < 0 || to < 0) return;
      const nextGroup = [...inGroup];
      nextGroup.splice(to, 0, ...nextGroup.splice(from, 1));
      // Write the group's new order back into the flat list, slot by slot.
      const slots = current.reduce<number[]>((acc, c, index) => {
        if (group.items.some((i) => i.id === c)) acc.push(index);
        return acc;
      }, []);
      const next = [...current];
      slots.forEach((slot, index) => {
        next[slot] = nextGroup[index]!;
      });
      save(next);
    },
    [save],
  );

  const reset = useCallback(() => save(DEFAULT_PRIMARY), [save]);

  const relabel = useCallback(
    (i: NavItem): NavItem =>
      i.id === "revenue:contracts" ? { ...i, label: features.contractsLabel } : i,
    [features.contractsLabel],
  );

  const groups = useMemo<NavGroupView[]>(
    () =>
      NAV_GROUPS.map((group) => ({
        key: group.key,
        label: group.label,
        icon: group.icon,
        items: primary
          .map((id) => group.items.find((i) => i.id === id))
          .filter((i): i is NavItem => Boolean(i))
          .map(relabel),
        hidden: group.items.filter((i) => !primary.includes(i.id)).map(relabel),
      })),
    [primary, relabel],
  );


  return { primary, groups, toggle, reorder, move, reset };
}

export function isActivePath(pathname: string, to: string) {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/** True when this menu item is the best match for the current path. */
export function isActiveItem(pathname: string, to: string) {
  if (to === "/sales") return pathname === "/sales";
  if (to === "/work") return pathname === "/work";
  return isActivePath(pathname, to);
}
